/**
 * TenantViewDetailsDialog
 * Read-only dialog showing side-by-side comparison of base and target tenant rules
 */

import React from 'react';
import {
  Button,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { RuleRow } from './RuleRow';
import { DIALOG_STYLES, CONTAINER_STYLES, SECTION_HEADER_STYLES, COLORS, TYPOGRAPHY } from '../constants';
import { convertTenantRuleToPolicyRule } from '../utils';
import { TenantPolicy, TenantBaseRule, TenantTargetRule } from '../types';

interface TenantViewDetailsDialogProps {
  isOpen: boolean;
  policy: TenantPolicy | null;
  baseTenantName: string;
  targetTenantName: string;
  expandedRuleIds: Set<string>;
  onToggleRuleExpand: (ruleId: string) => void;
  onClose: () => void;
}

export function TenantViewDetailsDialog({
  isOpen,
  policy,
  baseTenantName,
  targetTenantName,
  expandedRuleIds,
  onToggleRuleExpand,
  onClose,
}: TenantViewDetailsDialogProps) {
  if (!policy) return null;

  const baseRules = policy.baseRules;
  const targetRules = policy.targetRules || [];
  const isAvailable = policy.status === 'Deployed' || policy.status === 'PartiallyDeployed';
  
  // A rule is deployed if isDeployed is true OR if it exists in targetRules
  const deployedFromBase = baseRules.filter(r => r.isDeployed).map(r => r.ruleId);
  const deployedFromTarget = targetRules.map(r => r.ruleId);
  const deployedRuleIds = new Set([...deployedFromBase, ...deployedFromTarget]);

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => data.open ? null : onClose()}>
      <DialogSurface style={DIALOG_STYLES.surfaceLarge}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close dialog"
                title="Close dialog"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            {policy.policyName} – Rule Comparison (Base Tenant → {targetTenantName})
          </DialogTitle>
          <DialogContent style={DIALOG_STYLES.contentFullHeight}>
            {/* Info message */}
            <div style={CONTAINER_STYLES.infoMessage}>
              <Text style={{ ...TYPOGRAPHY.body, color: COLORS.gray600, lineHeight: 1.5 }}>
                A side-by-side comparison of the selected policy's rules, SITs, and sensitivity labels in the base/template tenant ({baseTenantName}) and the target tenant ({targetTenantName}). This is a read-only view.
              </Text>
            </div>
            
            {/* Two column layout - Base and Target rules side by side */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
              {/* Base Policy Rules Column */}
              <div style={CONTAINER_STYLES.panelWithBorder}>
                <div style={SECTION_HEADER_STYLES.sticky}>
                  Base / Template Tenant ({baseTenantName})
                </div>
                {baseRules.map((rule) => {
                  const isDeployedToTarget = deployedRuleIds.has(rule.ruleId);
                  const comparisonStatus = isAvailable 
                    ? (isDeployedToTarget ? 'deployed' : 'missing') 
                    : 'none';
                  
                  return (
                    <RuleRow
                      key={rule.ruleId}
                      rule={convertTenantRuleToPolicyRule(rule)}
                      isExpanded={expandedRuleIds.has(rule.ruleId)}
                      onToggle={() => onToggleRuleExpand(rule.ruleId)}
                      comparisonStatus={comparisonStatus as 'deployed' | 'missing' | 'none'}
                      variant="readonly"
                    />
                  );
                })}
                {baseRules.length === 0 && (
                  <div style={CONTAINER_STYLES.emptyState}>
                    No base rules defined
                  </div>
                )}
              </div>
              
              {/* Target Rules Column */}
              <div style={CONTAINER_STYLES.panel}>
                <div style={SECTION_HEADER_STYLES.sticky}>
                  Target Tenant: {targetTenantName}
                </div>
                {isAvailable ? (
                  targetRules.map((rule) => (
                    <RuleRow
                      key={`target-${rule.ruleId}`}
                      rule={convertTenantRuleToPolicyRule(rule)}
                      isExpanded={expandedRuleIds.has(`target-${rule.ruleId}`)}
                      onToggle={() => onToggleRuleExpand(`target-${rule.ruleId}`)}
                      variant="readonly"
                    />
                  ))
                ) : null}
                {(!isAvailable || targetRules.length === 0) && (
                  <div style={CONTAINER_STYLES.emptyState}>
                    {isAvailable ? 'No rules deployed to this tenant' : 'Policy not deployed to this tenant'}
                  </div>
                )}
              </div>
            </div>
            
            {/* Close Button Only (no deploy) */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                padding: '16px 24px',
                borderTop: '1px solid #e5e5e5',
                background: 'white',
              }}
            >
              <Button appearance="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
